import React, { useState } from 'react';
import { useDispatch, useStore, useSelector } from 'react-redux';

import {
  Modelo,
  WindxPreferencia,
  SalvarPreferenciaRequest,
  PreferenciasJsonSH,
  PreferenciasJsonSM,
  TipoRodada,
  WindxTela,
  TipoPreferenciaApi,
  painelExibeTipoRodada,
} from 'helper/windx';
import {
  useGetPainelOptionsQuery,
  useGetPreferenciasQuery,
  useSavePreferenciaMutation,
  useDeletePreferenciaMutation,
} from 'services/api/windx';
import {
  realizarConsulta,
  selectWindxFiltrosCompletos,
  setIdsModelos,
  setOpcoesRmv,
  setTipoRodada,
  setPatamar,
  setUnidade,
  setIdSubdivisaoEspacial,
  setIdsEntidades,
  setIdsSimulacoesAntigasAtivas,
  setFatorRen,
  selectWindxGranularidadeSH,
} from 'redux/reducers/windxControlPanelReducer';

// WINDX-030: snapshot.opcoesRmv eh array para WEOL-SH.
// WEOL-SM mantem semantica de 1 item (regra apenasComRmv).
export interface SavedPreferencesSnapshot {
  idsModelos: number[];
  opcoesRmv: ('com_rmv' | 'sem_rmv')[];
  // WINDX-056: pode ser undefined no boot do WEOL-SH (sem default). No save
  // aplicamos fallback concreto (uma preferencia precisa ser restauravel).
  tipoRodada: PreferenciasJsonSH['tipo_rodada'] | undefined;
  patamar: PreferenciasJsonSM['patamar'];
  unidade: PreferenciasJsonSH['unidade'];
  idSubdivisao: number | undefined;
  idsEntidades: number[];
  idsSimulacoesAntigasAtivas: number[];
  fatorRen: boolean;
}

export interface UseSavedPreferencesArgs {
  tela: Extract<WindxTela, 'sh' | 'sm' | 'comparativo'>;
  snapshot: SavedPreferencesSnapshot;
  onCloseSaveForm: () => void;
}

// WEOL-SM (#99): o id 14 (ONS_Preliminar) foi unificado no 13, que passou a ter as
// duas rodadas (change B do Sqitch deixa o 14 inativo). Preferencias salvas antes
// ainda o trazem.
const ID_ONS_PRELIMINAR = 14;
// Default do Comparativo (radio), o mesmo do reducer (setTelaAtiva e Limpar filtros).
const ID_MODELO_DEFAULT_COMPARATIVO = 13;

/**
 * Ids de uma preferencia do Semanal/Comparativo que ainda valem: sem o 14 e sem
 * modelo que saiu do painel-options (desativado no admin ou fora da tela). Se nao
 * sobrar nenhum, o Comparativo cai no default [13]; o Semanal fica vazio, e o gate
 * do auto-disparo segura a consulta (o painel pede o modelo).
 */
function idsModelosRestauraveisSM(
  idsSalvos: number[],
  modelosPainel: Pick<Modelo, 'id'>[],
  tela: 'sm' | 'comparativo',
): number[] {
  const noPainel = new Set(modelosPainel.map((m) => m.id));
  const validos = idsSalvos.filter((id) => id !== ID_ONS_PRELIMINAR && noPainel.has(id));
  if (validos.length === 0 && tela === 'comparativo') return [ID_MODELO_DEFAULT_COMPARATIVO];
  return validos;
}

/**
 * Estado e acoes das Preferencias Salvas do Windx, compartilhados entre o
 * painel desktop (`SavedPreferences`, casca `Accordion.Item`) e o painel mobile
 * (`Windx/Mobile/ControlPanelSheet/Preferencias`, casca `Section`).
 *
 * A extracao existe para que as duas cascas nao possam divergir nas regras de
 * restauracao (WINDX-030/046/056, FE-WINDX-045, o auto-disparo da consulta e, desde
 * o WEOL-SM #99, o tipo de rodada e os ids validos do Semanal/Comparativo).
 * Chame o hook UMA vez por painel: a lista e o formulario precisam da mesma
 * instancia — `nome` e o estado das mutations vivem aqui.
 */
export default function useSavedPreferences({
  tela,
  snapshot,
  onCloseSaveForm,
}: UseSavedPreferencesArgs) {
  const dispatch = useDispatch();
  // useSelector congela o valor no render (closure). handleRestore dispara
  // varios actions que alteram `filtrosCompletos`; lemos o store apos as
  // dispatches para decidir se auto-disparamos a consulta.
  const store = useStore();
  const [nome, setNome] = useState('');

  const { data: todasPrefs, isLoading: isLoadingList } = useGetPreferenciasQuery();
  const [savePreferencia, { isLoading: isSaving }] = useSavePreferenciaMutation();
  const [deletePreferencia, { isLoading: isDeleting }] = useDeletePreferenciaMutation();

  const granularidadeSH = useSelector(selectWindxGranularidadeSH);
  // WINDX-046: o tipo no contrato/BD separa WEOL-SH por sub-aba. O BE rejeita
  // 'sh' (so aceita 'sh-semi'/'sh-diario'/'sm'/'comparativo'). `tela` continua
  // sendo o eixo logico ('sh'/'sm'/'comparativo') usado no restore; aqui
  // derivamos o tipo da API para salvar e para filtrar a lista por sub-aba.
  const tipoSH: TipoPreferenciaApi = granularidadeSH === 'diario' ? 'sh-diario' : 'sh-semi';
  const tipoApi: TipoPreferenciaApi = tela === 'sh' ? tipoSH : tela;

  // WEOL-SM (#99): o restore do Semanal/Comparativo usa o painel-options do SM (o
  // mesmo cache que o painel ja assina, sem request extra) para descartar os ids que
  // nao valem mais e saber se a etapa Tipo de Rodada existe. No SH nao consulta
  // (skip): o restore do SH nao mudou.
  const { data: painelSM } = useGetPainelOptionsQuery('sm', { skip: tela === 'sh' });
  // Sem esse painel, a lista do Semanal/Comparativo fica no spinner: restaurar antes
  // dele levaria o 14 (ou um modelo desativado) para a consulta automatica. No app o
  // painel ja chegou quando a lista aparece; a espera so cobre a janela da troca de
  // aba, em que o painel da aba anterior ainda esta na tela.
  // Isso depende dos pais (ControlPanel e Mobile/ControlPanelSheet): se o painel do SM
  // falhar, eles mostram o Alert de erro (`error || !opts`) no lugar de montar a lista.
  // Sem esse Alert, o spinner ficaria para sempre, porque `painelSM` nunca chegaria.
  const aguardandoPainelSM = tela !== 'sh' && !painelSM;

  const prefs = aguardandoPainelSM ? [] : (todasPrefs ?? []).filter((p) => p.tipo === tipoApi);

  const handleRestore = (pref: WindxPreferencia) => {
    const j = pref.preferencias_json;
    // WEOL-SM (#99): no Semanal/Comparativo os ids passam pelo painel-options ANTES do
    // setIdsModelos e do auto-disparo abaixo, para a consulta nunca sair com o 14 nem
    // com modelo desativado. O tipo salvo volta junto; preferencia de antes do campo
    // volta 'final' (o default do BE). Sem a etapa Tipo de Rodada no painel (flag do
    // BE desligada) vale sempre 'final', como o useControlPanelRules assume (ADR-056,
    // item 10): um 'preliminar' restaurado ficaria sem radio para trocar.
    let idsModelos = j.id_modelos;
    let tipoRodadaSM: TipoRodada | undefined;
    if (tela !== 'sh') {
      // Guarda de tipo: a lista so aparece com o painel (`aguardandoPainelSM`).
      if (!painelSM) return;
      idsModelos = idsModelosRestauraveisSM(j.id_modelos, painelSM.modelos, tela);
      tipoRodadaSM = painelExibeTipoRodada(painelSM, 'sm') ? (j.tipo_rodada ?? 'final') : 'final';
    }
    dispatch(setIdsModelos(idsModelos));
    dispatch(setIdSubdivisaoEspacial(j.subdivisao_espacial));
    dispatch(setIdsEntidades(j.id_entidades));
    // WINDX-030: PreferenciasJsonSH usa opcoes_rmv (array); PreferenciasJsonSM
    // ainda usa com_rmv (boolean — apenasComRmv sempre true).
    if (tela === 'sh' && 'opcoes_rmv' in j) {
      dispatch(setOpcoesRmv(j.opcoes_rmv));
    } else if ('com_rmv' in j) {
      // SM/Comparativo: converte boolean -> [unica opcao]
      dispatch(setOpcoesRmv([j.com_rmv ? 'com_rmv' : 'sem_rmv']));
    }
    dispatch(setUnidade(j.unidade));
    // WEOL-SM (#99): PreferenciasJsonSM tambem tem `tipo_rodada` (opcional),
    // entao o `in` nao estreita mais para o SH; checar o valor garante um
    // TipoRodada concreto (no SH o campo e obrigatorio — nada muda).
    if (tela === 'sh' && 'tipo_rodada' in j && j.tipo_rodada) {
      dispatch(setTipoRodada(j.tipo_rodada));
    }
    // Semanal/Comparativo: o tipo resolvido no inicio (o salvo ou 'final').
    if (tipoRodadaSM) {
      dispatch(setTipoRodada(tipoRodadaSM));
    }
    // Comparativo tambem usa patamar (mesma granularidade do Semanal).
    if ((tela === 'sm' || tela === 'comparativo') && 'patamar' in j) {
      dispatch(setPatamar(j.patamar));
    }
    // FE-WINDX-045 (restaurar simulacoes antigas ativas): completa o par
    // do handleSave que ja persistia o array. Sem isto, recarregar uma
    // preferencia com sim antiga marcada perdia a selecao (bug PO 2026-05-27).
    // Campo so existe em PreferenciasJsonSH; SM/Comparativo nao tem sim antiga.
    if (tela === 'sh' && 'ids_simulacoes_antigas_ativas' in j) {
      dispatch(setIdsSimulacoesAntigasAtivas(j.ids_simulacoes_antigas_ativas ?? []));
    }
    // REN (2026-05-29): aplica em SH e SM. Campo opcional — prefs antigas
    // sem ele preservam o default (false), sem disparar setFatorRen.
    if ('incluir_fator_ren' in j) {
      dispatch(setFatorRen(!!j.incluir_fator_ren));
    }
    // Pref salva tem todos os filtros obrigatorios — dispara a consulta
    // automaticamente para nao mostrar "Nenhum resultado" enganoso (bug PO
    // 2026-05-27 em Comparativo: usuario clicava na pref e via empty state
    // ate clicar Realizar Consulta manualmente). Gate defensivo: se filtros
    // estiverem incompletos (pref legada / malformada), apenas marca pendente.
    if (selectWindxFiltrosCompletos(store.getState())) {
      dispatch(realizarConsulta());
    }
  };

  const handleDelete = (id: number) => {
    deletePreferencia(id);
  };

  const handleDismiss = () => {
    setNome('');
    onCloseSaveForm();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) return;

    const jsonSH: PreferenciasJsonSH = {
      frequencia_temporal: 'sh',
      subdivisao_espacial: snapshot.idSubdivisao ?? 1,
      id_entidades: snapshot.idsEntidades,
      id_modelos: snapshot.idsModelos,
      unidade: snapshot.unidade,
      // WINDX-056: fallback defensivo 'final' (mesma logica do `?? 1` /
      // `?? ['com_rmv']` abaixo). No boot SH o tipoRodada pode ser undefined;
      // uma preferencia salva precisa de um tipo concreto para restaurar a um
      // estado funcional. Espelha o default do BE (ChartDataRequest).
      tipo_rodada: snapshot.tipoRodada ?? 'final',
      // WINDX-030: opcoes_rmv array completo. Min 1 (garantido pelo Rmv).
      opcoes_rmv: snapshot.opcoesRmv.length > 0 ? snapshot.opcoesRmv : ['com_rmv'],
      ids_simulacoes_antigas_ativas: snapshot.idsSimulacoesAntigasAtivas,
      incluir_fator_ren: snapshot.fatorRen,
    };

    const jsonSM: PreferenciasJsonSM = {
      subdivisao_espacial: snapshot.idSubdivisao ?? 1,
      id_entidades: snapshot.idsEntidades,
      id_modelos: snapshot.idsModelos,
      unidade: snapshot.unidade,
      patamar: snapshot.patamar,
      // SM continua boolean (apenasComRmv -> sempre true).
      com_rmv: snapshot.opcoesRmv.includes('com_rmv'),
      // WEOL-SM (#99): mesmo fallback 'final' do SH. Com a flag do BE desligada o
      // painel ja assume 'final' (useControlPanelRules), entao o tipo quase sempre
      // existe aqui.
      tipo_rodada: snapshot.tipoRodada ?? 'final',
      incluir_fator_ren: snapshot.fatorRen,
    };

    const request: SalvarPreferenciaRequest = {
      nome: nome.trim(),
      tipo: tipoApi,
      preferencias_json: tela === 'sh' ? jsonSH : jsonSM,
    };

    await savePreferencia(request);
    setNome('');
    onCloseSaveForm();
  };

  return {
    prefs,
    isLoadingList: isLoadingList || aguardandoPainelSM,
    isDeleting,
    isSaving,
    nome,
    setNome,
    handleRestore,
    handleDelete,
    handleDismiss,
    handleSave,
  };
}
